import { useTranslation } from 'react-i18next'

import { Dialog } from '@front/components/feedback/dialog/Dialog'
import { RadioList } from '@front/components/inputs/lists/radio-list/RadioList'

import { useJobAdsFiltersStorage } from '../hooks/useJobAdsFiltersStorage'

import { useFiltersSelection } from '../hooks/useFiltersSelectionTemplate'
import { Box } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import type { JobAdsFormType } from '../../types/career.types'
import { capitalizeFirst } from '@hublo/utils/string'

type Props = {
  isOpen: boolean
  onClose: () => void
}

export const SortingDialog = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation(['main.common', 'main.worker'])
  const { storageData, setStorageData } = useJobAdsFiltersStorage()
  const { setValue, getValues } = useFormContext<JobAdsFormType>()

  const { handleItemClick, localValue, resetLocalValue, saveValue } =
    useFiltersSelection({
      isMultiSelection: false,
      typeKey: 'sortBy',
    })

  const items = [
    {
      itemId: 'start_date',
      label: t('main.worker:job_offers.filter.contract_start_date'),
      selected: 'start_date' === localValue,
      'data-testid': 'JobAds__SortingDialog__option_contract_start_date',
    },
    {
      itemId: 'published_at',
      label: t('main.worker:job_offers.filter.publication_date'),
      selected: 'published_at' === localValue,
      'data-testid': 'JobAds__SortingDialog__option_publication_date',
    },
  ]

  const handleCancel = () => {
    resetLocalValue()
    onClose()
  }

  const handleValidate = () => {
    saveValue()
    const newSortOrder = getValues('sortOrder') === 'asc' ? 'desc' : 'asc'
    setValue('sortOrder', newSortOrder)
    setStorageData({
      ...storageData,
      sortBy: localValue as string,
      sortOrder: newSortOrder,
    })
    onClose()
  }

  return (
    <Dialog
      bottomActions={{
        secondaryButtonProps: {
          variant: 'secondary',
          label: capitalizeFirst(t('main.common:cancel')),
          onClick: handleCancel,
          'data-testid': 'JobAds__SortingDialog__btn_cancel',
        },
        primaryButtonProps: {
          variant: 'primary',
          label: capitalizeFirst(t('main.common:validate')),
          onClick: handleValidate,
          'data-testid': 'JobAds__SortingDialog__btn_validate',
        },
      }}
      content={
        <Box
          sx={{
            mx: 2,
          }}
        >
          <RadioList data={items} onClick={handleItemClick} />
        </Box>
      }
      data-testid="JobAds__SortingDialog"
      isOpen={isOpen}
      onClose={handleCancel}
      title={t('main.worker:job_offers.filter.sort_by')}
      variant="bottomSheet"
      withGutters={false}
    />
  )
}
